/* vpv-filters-buy-box.js
 * Wires the custom filter cards to the existing CBB form on the filters product.
 *
 *  1. Listens for `cbb:variant:changed` on the CBB section (dispatched by
 *     <cbb-variants> AFTER it has resolved the variant and written #id), and
 *     shows the card group for that variant, hiding the rest.
 *  2. Within the active group, manages which card (subscription vs one-time) is
 *     selected. The ENTIRE card is clickable, not just the radio/header.
 *  3. Writes the chosen variant id + selling plan id into the CBB form inputs and
 *     fires `change` so vpv-cbb-product-form.js recomputes price + ATC label.
 *
 * Consumes the resolved variant from the event rather than racing the pill
 * listeners, so ordering is guaranteed regardless of script load order.
 */
class VpvFiltersBuyBox extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId);
    this.section = this.closest('[data-cbb-section]');
    this.idInput =
      this.form &&
      (this.form.querySelector('[data-cbb-variant-input]') ||
        this.form.querySelector('[name="id"]'));
    this.planInput =
      (this.form && this.form.querySelector('[name="selling_plan"]')) ||
      this.querySelector('[data-vpv-fc-plan-input]');
    this.groups = Array.from(this.querySelectorAll('[data-vpv-fc-group]'));

    this._onCardClick = this._onCardClick.bind(this);
    this._onVariantChanged = this._onVariantChanged.bind(this);

    this.addEventListener('click', this._onCardClick);
    if (this.section) {
      this.section.addEventListener('cbb:variant:changed', this._onVariantChanged);
    }

    // Set up the initial selection for the variant shown on first paint.
    // writeForm:false avoids re-firing change on load (the form already has the id).
    const initial = this.dataset.selectedVariant;
    if (initial) this.selectVariant(initial, { writeForm: false });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onCardClick);
    if (this.section) {
      this.section.removeEventListener('cbb:variant:changed', this._onVariantChanged);
    }
  }

  _onVariantChanged(event) {
    const variant = event.detail && event.detail.variant;
    if (!variant || variant.id == null) return;
    // <cbb-variants> already wrote #id from this event; we swap the visible group
    // and select that group's default card, which also writes the selling plan.
    this.selectVariant(variant.id, { writeForm: true });
  }

  selectVariant(variantId, { writeForm = true } = {}) {
    let matched = null;
    this.groups.forEach((group) => {
      const isMatch = group.dataset.variantId === String(variantId);
      group.toggleAttribute('hidden', !isMatch);
      if (isMatch) matched = group;
    });
    if (!matched) return;

    const defaultCard =
      matched.querySelector('[data-vpv-fc-default]') ||
      matched.querySelector('[data-vpv-fc-card]');
    if (defaultCard) this.selectCard(defaultCard, { writeForm });
  }

  _onCardClick(event) {
    // Whole card is clickable, not just the header/radio.
    const card = event.target.closest('[data-vpv-fc-card]');
    if (card && this.contains(card)) this.selectCard(card);
  }

  selectCard(card, { writeForm = true } = {}) {
    const group = card.closest('[data-vpv-fc-group]');
    if (group) {
      group.querySelectorAll('[data-vpv-fc-card]').forEach((c) => {
        const active = c === card;
        c.classList.toggle('vpv-fc__card--active', active);
        const header = c.querySelector('.vpv-fc__header');
        if (header) header.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }
    this.writeForm(card, !writeForm);
  }

  writeForm(card, silent) {
    if (!this.form) return;
    const variantId = card.dataset.variantId;
    const planId = card.dataset.sellingPlanId || '';

    if (this.idInput) this.idInput.value = variantId;
    if (this.planInput) this.planInput.value = planId;

    this.dataset.selectedVariant = variantId;

    if (silent) return;
    // Fire change on the selling plan input so the form JS recomputes the
    // selling-plan-aware price. (Don't re-fire #id here: <cbb-variants> already
    // did, and the form's price refresh reads both inputs anyway.)
    if (this.planInput) this.planInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('vpv-filters-buy-box')) {
  customElements.define('vpv-filters-buy-box', VpvFiltersBuyBox);
}
